import { useDateFormat } from "Infrastructure/Utilities/dateFormat";
import { useOrderPartnerAllocationsQuery } from "Order/Api/Query/useOrderPartnerAllocationsQuery";
import type { PartnerAllocationHistoryItem } from "Order/Utilities/partnerAllocationHistory";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import {
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";

type PartnerAllocationHistoryWidgetProps = {
	orderId: number;
};

function formatSource(source: PartnerAllocationHistoryItem["source"]) {
	return source
		.split("_")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
}

export function PartnerAllocationHistoryWidget({
	orderId,
}: PartnerAllocationHistoryWidgetProps) {
	const { formatDateTime } = useDateFormat();
	const { data: allocationHistory = [], isLoading } =
		useOrderPartnerAllocationsQuery(orderId);

	const getPartnerName = (allocation: PartnerAllocationHistoryItem) => {
		if (allocation.partner?.fullName) {
			return allocation.partner.fullName;
		}

		return `Partner #${allocation.partnerId}`;
	};

	const sortedAllocationHistory = [...allocationHistory].sort((a, b) =>
		b.assignedAt.localeCompare(a.assignedAt),
	);

	return (
		<BaseCard title="Partner allocation history" isLoading={isLoading}>
			{isLoading ? (
				<></>
			) : sortedAllocationHistory.length === 0 ? (
				<Typography color="text.secondary">
					No partner allocation history yet.
				</Typography>
			) : (
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Partner</TableCell>
								<TableCell>Assigned at</TableCell>
								<TableCell>Unassigned at</TableCell>
								<TableCell>Source</TableCell>
								<TableCell>Reason</TableCell>
								<TableCell>Status</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{sortedAllocationHistory.map((allocation) => {
								const isCurrent =
									allocation.isCurrent === true ||
									allocation.unassignedAt === null;

								return (
									<TableRow key={allocation.id}>
										<TableCell>{getPartnerName(allocation)}</TableCell>
										<TableCell>
											{formatDateTime(allocation.assignedAt)}
										</TableCell>
										<TableCell>
											{allocation.unassignedAt
												? formatDateTime(allocation.unassignedAt)
												: "-"}
										</TableCell>
										<TableCell>{formatSource(allocation.source)}</TableCell>
										<TableCell>{allocation.reason || "-"}</TableCell>
										<TableCell>
											<Chip
												size="small"
												color={isCurrent ? "success" : "default"}
												label={isCurrent ? "Current" : "Past"}
											/>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</BaseCard>
	);
}
